/* Dateien teilen: Auswahl der Spielerkarten, Dateinamen, Laden mit Zeitgrenze, Teilen oder Herunterladen.
 *
 * Reine Funktionen ohne Vue und ohne DOM-Zugriff; Browser-Teile (navigator, Download) kommen als Parameter
 * herein. So lässt sich jeder Weg prüfen: Teilen-Menü, abgelaufene Berührung, Download-Rückfall, Abbruch.
 * Verwendet in composables/useShareFiles.js (Player Card als erzeugtes PDF; hochgeladene Karten gibt es nicht mehr).
 *
 * Datenschutz: Hier steht nur der Ablauf. Welche Dateien überhaupt gemeint sind, bestimmen die Aufrufer.
 */


/** Höchstens so viele Dateien gleichzeitig laden (Hotel-Netz, Hotspot). */
export const LOAD_CONCURRENCY = 3;
/** Zeitgrenze je Datei (Millisekunden). */
export const LOAD_TIMEOUT_MS = 30000;
/** Pause zwischen zwei Einzel-Downloads, damit der Browser sie nicht als Flut blockiert. */
export const DOWNLOAD_GAP_MS = 400;

/* ------------------------------------------------------------- Dateinamen */

/** Teil eines Dateinamens: ohne Akzente (ọ -> o, ṣ -> s), nur Buchstaben und Ziffern, Wörter mit "_". */
export function filePart(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * "Vorname_Nachname_<Zusatz>.pdf", bereinigt und eindeutig (bei gleichen Namen _2, _3 …).
 *
 * @param {{ firstName?: string, lastName?: string }} player
 * @param {string} suffix  z. B. 'PlayerCard' oder 'Profile'
 * @param {Set<string>} used  schon vergebene Namen (kleingeschrieben); wird ergänzt
 */
export function playerFilename(player, suffix, used = new Set()) {
  const name = [filePart(player && player.firstName), filePart(player && player.lastName)].filter(Boolean).join('_') || 'Player';
  const base = `${name}_${suffix}`;
  let candidate = `${base}.pdf`;
  let n = 2;
  while (used.has(candidate.toLowerCase())) candidate = `${base}_${n++}.pdf`;
  used.add(candidate.toLowerCase());
  return candidate;
}

/* ----------------------------------------------------------------- Laden */

/** Abbruch-Zeichen: cancel() löst `promise` aus, `cancelled` ist danach wahr. */
export function createCancel() {
  let resolve;
  const promise = new Promise((r) => { resolve = r; });
  const token = {
    cancelled: false,
    promise,
    cancel() {
      if (token.cancelled) return;
      token.cancelled = true;
      resolve('cancelled');
    },
  };
  return token;
}

/** Eine Aufgabe mit Zeitgrenze; der Zeitgeber wird immer wieder abgeräumt. */
export function withTimeout(promise, ms, label = 'timeout') {
  let timer;
  const limit = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([promise, limit]).finally(() => clearTimeout(timer));
}

/**
 * Dateien laden: höchstens `concurrency` gleichzeitig, je Datei eine Zeitgrenze. Eine fehlgeschlagene Datei
 * stoppt die anderen nicht; sie steht am Ende in `failed`. Bei Abbruch kehrt die Funktion sofort zurück
 * (laufende Ladevorgänge werden ignoriert), `cancelled` ist wahr.
 *
 * @param {Array<{ name: string, filename: string, load: () => Promise<Blob> }>} items
 * @param {{ concurrency?: number, timeoutMs?: number, cancel?: ReturnType<createCancel>,
 *           onProgress?: (done: number, total: number) => void }} [opts]
 * @returns {Promise<{ files: Array<{ filename: string, blob: Blob }>, failed: string[], cancelled: boolean }>}
 */
export async function loadFiles(items, { concurrency = LOAD_CONCURRENCY, timeoutMs = LOAD_TIMEOUT_MS, cancel = createCancel(), onProgress = () => {} } = {}) {
  const pool = await mapPool(
    items,
    async (item) => {
      const blob = await item.load();
      if (!blob) throw new Error('empty');
      return { filename: item.filename, blob };
    },
    { concurrency, timeoutMs, cancel, onProgress }
  );
  const failed = [];
  pool.errors.forEach((err, i) => {
    if (err) {
      console.error('[share] Datei konnte nicht geladen werden:', items[i].filename, err && err.message);
      failed.push(items[i].name);
    }
  });
  if (pool.cancelled) return { files: [], failed, cancelled: true };
  return { files: pool.results.filter(Boolean), failed, cancelled: false };
}

/**
 * Aufgaben mit begrenzter Gleichzeitigkeit, Zeitgrenze je Aufgabe und Abbruch ausführen — die gemeinsame
 * Grundlage für das Laden von Dateien (loadFiles) und das Vorbereiten von Profilen (Stufe 4).
 * Eine fehlgeschlagene Aufgabe stoppt die anderen nicht: ihr Fehler steht in `errors[i]`.
 * Bei Abbruch kehrt die Funktion sofort zurück (laufende Aufgaben werden ignoriert).
 *
 * @template T, R
 * @param {T[]} list
 * @param {(item: T, index: number) => Promise<R>} worker
 * @returns {Promise<{ results: Array<R|null>, errors: Array<Error|null>, cancelled: boolean }>}
 */
export async function mapPool(list, worker, { concurrency = LOAD_CONCURRENCY, timeoutMs = LOAD_TIMEOUT_MS, cancel = createCancel(), onProgress = () => {} } = {}) {
  const total = list.length;
  const results = new Array(total).fill(null);
  const errors = new Array(total).fill(null);
  let next = 0;
  let done = 0;
  onProgress(0, total);

  async function lane() {
    while (!cancel.cancelled) {
      const i = next++;
      if (i >= total) return;
      try {
        results[i] = await withTimeout(Promise.resolve().then(() => worker(list[i], i)), timeoutMs);
      } catch (e) {
        errors[i] = e || new Error('failed');
      }
      done += 1;
      if (!cancel.cancelled) onProgress(done, total);
    }
  }

  const lanes = Array.from({ length: Math.min(concurrency, total) }, () => lane());
  await Promise.race([Promise.all(lanes), cancel.promise]);
  return { results, errors, cancelled: cancel.cancelled };
}

/* ---------------------------------------------------------------- Teilen */

/** Blob + Dateiname -> File (so verlangt es navigator.share). */
export function toFile({ filename, blob }, FileCtor = globalThis.File) {
  return new FileCtor([blob], filename, { type: 'application/pdf' });
}

/**
 * Dateien übergeben: Teilen-Menü (Handy), sonst einzelne Downloads.
 *
 *  'shared'      navigator.share lief durch
 *  'dismissed'   Nutzer hat das Teilen-Menü geschlossen (AbortError) — kein Fehler, keine Meldung
 *  'needsTap'    die Berührung war schon abgelaufen (NotAllowedError); `files` für einen frischen Tipp aufheben
 *  'downloaded'  keine Teilen-Funktion für Dateien: jede Datei einzeln heruntergeladen
 *
 * @param {Array<{ filename: string, blob: Blob }>} files
 * @param {{ nav?: object, download: (blob: Blob, filename: string) => void, wait?: (ms: number) => Promise<void>,
 *           FileCtor?: Function }} env
 * @returns {Promise<{ status: string, count: number, files: File[] }>}
 */
export async function deliverFiles(files, { nav = globalThis.navigator, download, wait = (ms) => new Promise((r) => setTimeout(r, ms)), FileCtor } = {}) {
  const count = files.length;
  const real = files.map((f) => toFile(f, FileCtor));

  if (count && nav && typeof nav.canShare === 'function' && typeof nav.share === 'function' && nav.canShare({ files: real })) {
    try {
      await nav.share({ files: real });
      return { status: 'shared', count, files: real };
    } catch (e) {
      if (e && e.name === 'AbortError') return { status: 'dismissed', count, files: real };
      if (e && e.name === 'NotAllowedError') return { status: 'needsTap', count, files: real };
      console.error('[share] Teilen fehlgeschlagen, lade einzeln herunter:', e && e.message);
      /* andere Fehler: lieber herunterladen als nichts liefern */
    }
  }

  for (let i = 0; i < files.length; i += 1) {
    download(files[i].blob, files[i].filename);
    if (i < files.length - 1) await wait(DOWNLOAD_GAP_MS);
  }
  return { status: 'downloaded', count, files: real };
}

/**
 * Frischer Tipp nach 'needsTap': dieselben Dateien noch einmal übergeben. Läuft auch das nicht
 * (NotAllowedError), werden sie heruntergeladen.
 */
export async function shareAgain(files, { nav = globalThis.navigator, download, wait } = {}) {
  try {
    await nav.share({ files });
    return { status: 'shared', count: files.length };
  } catch (e) {
    if (e && e.name === 'AbortError') return { status: 'dismissed', count: files.length };
    for (let i = 0; i < files.length; i += 1) {
      download(files[i], files[i].name);
      if (i < files.length - 1) await (wait || ((ms) => new Promise((r) => setTimeout(r, ms))))(DOWNLOAD_GAP_MS);
    }
    return { status: 'downloaded', count: files.length };
  }
}
